/**
 * Texto que la fuente del informe sabe dibujar.
 *
 * ── El fallo que esto arregla ──────────────────────────────────────────────
 *
 * En el informe, «C-25 (82+800 → 236+000)» salía como
 * «C-25 (82+800 '236+000)»: en vez de la flecha, una comilla.
 *
 * La causa no es el PDF, es la FUENTE. Los informes van en **Poppins**, y
 * Poppins **no tiene ni una sola flecha** — comprobado con fontkit sobre el
 * propio `.ttf` que servimos: 472 caracteres, todos latinos, y ninguno de
 * `→ ⟶ ⇒ ➔ ➜ ↦ ▶ ▸ ‣`.
 *
 * Cuando falta el glifo, react-pdf cae a las fuentes estándar del PDF, que van
 * en **WinAnsi** (cp1252) — un juego de 8 bits. Ahí `→` (U+2192) no existe, y
 * al codificarlo se queda con el byte bajo: `0x2192 & 0xFF = 0x92`, que en
 * cp1252 es **’**, la comilla simple derecha. De ahí la comilla, y de ahí que
 * no diera ningún error: para el PDF era un carácter perfectamente válido.
 *
 * ── Por qué se sustituye en vez de arreglar la fuente ──────────────────────
 *
 * `Font.register` de react-pdf (4.3.2) no tiene cadena de reserva: una familia
 * es un fichero, y no hay forma de decir «esta letra, con esta otra fuente».
 * Tener la flecha de verdad obliga a EMPAQUETAR otra fuente en la aplicación.
 * Mientras tanto, se escribe con un carácter que Poppins sí tiene.
 *
 * ── Y por qué esto es un mapa y no un `replace` suelto ─────────────────────
 *
 * Porque el siguiente que escriba `≥` o `←` en un informe se va a comer la
 * misma comilla, y **sin ningún aviso**. El mapa se prueba contra el `.ttf`
 * real: el test comprueba que Poppins NO tiene el original y SÍ el sustituto.
 */

/**
 * Lo que Poppins no sabe dibujar, y con qué se escribe en su lugar.
 *
 * Los sustitutos están medidos contra las Poppins que empaquetamos — no
 * elegidos de memoria. Para la flecha se comparó `» > › — ->` renderizados en
 * un PDF de verdad y se midió el glifo en la fuente: `>` es **un 28 % más
 * alto** que `»` (44,8 % del em contra 35,0 %) con el mismo tamaño de letra,
 * así que se lee de lejos sin tener que agrandarlo — y agrandarlo tenía un
 * precio: un trozo más grande ensancha la línea y baja el valor respecto a su
 * etiqueta, 0,42 pt por cada 0,05 de aumento (medido).
 */
export const PDF_SAFE_REPLACEMENTS: Record<string, string> = {
  '→': '>',
  '⟶': '>',
  '⇒': '>',
  '➔': '>',
  '➜': '>',
  '←': '<',
  '↔': '<>',
  '↕': '|',
  '↑': '^',
  '↓': 'v',
  // `≥ ≤ ≠ ≈ ×` NO están aquí a propósito: Poppins SÍ los tiene, comprobado
  // contra el `.ttf`. Sustituirlos sería empeorar el informe sin motivo — y el
  // test lo rechaza, que es justo lo que se le pide.
  '✓': 'OK',
  '✗': 'X',
  '⏳': '',
  '🔴': '',
  '⚠️': '',
  '⏸': '',
};

const PATRON = new RegExp(
  Object.keys(PDF_SAFE_REPLACEMENTS)
    .map((ch) => ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|'),
  'g'
);

/**
 * Devuelve el texto con los caracteres que la fuente del informe no tiene
 * cambiados por su equivalente. Lo que no está en el mapa se deja igual.
 */
export function pdfSafeText<T>(value: T): T | string {
  if (typeof value !== 'string') return value;
  return value.replace(PATRON, (ch) => PDF_SAFE_REPLACEMENTS[ch] ?? ch);
}

export default pdfSafeText;
